import React, { ReactNode, useEffect, useId, useRef, useState } from "react";
import application_block_icon from "../images/application-block.svg";
import CaretClose from "../images/app-caret-close.svg?react";
import CaretOpen from "../images/app-caret-open.svg?react";
import { List, ListItem, ListItemText, Tooltip, SvgIcon, Box, Typography, Link } from "@mui/material";
import {
	CoreIconButton,
	CoreModal,
	CoreNoResultsText,
	CorePaper,
	CoreSearchField,
	CoreTitleCard,
	CoreTextField,
	CoreSearchFieldPaper,
} from "../../core";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper } from "../../wrapper";
import styles from "./applicationListModal.module.css";
import Add from "../images/app-add.svg?react";
import Remove from "../images/app-remove.svg?react";
import AppUnknown from "../images/app-unknown.svg?react";
import { Application } from "@verity/proctoring-option/types";
import i18n from "../../i18n/i18n";
import { Trans } from "react-i18next";

const PAGE_SIZE = 9;
const LIST_PANEL_HEIGHT = PAGE_SIZE * 49;

function ApplicationRow({ application, action }: { application: Application; action: ReactNode }) {
	const textRef = useRef<HTMLDivElement>(null);
	const [overflow, setOverflow] = useState(false);
	useEffect(() => {
		if (textRef.current) {
			setOverflow(textRef.current.offsetWidth < textRef.current.scrollWidth);
		}
	}, [application.name]);

	let name = (
		<ListItemText
			className={styles.applicationName}
			primaryTypographyProps={{
				ref: textRef,
				className: styles.applicationName,
			}}
			primary={application.name}
		/>
	);
	if (overflow) {
		name = (
			<Tooltip title={application.name} arrow placement="top">
				{name}
			</Tooltip>
		);
	}

	return (
		<ListItem className={styles.applicationRow}>
			<div style={{ display: "inline-flex" }}>
				{application?.type !== "Other" && application.iconSvg ? (
					<div
						style={{ marginTop: "4px", marginRight: "4px", marginLeft: "4px" }}
						dangerouslySetInnerHTML={{ __html: application.iconSvg }}
					></div>
				) : (
					<AppUnknown
						style={{ marginTop: "6px", marginRight: "4px", marginLeft: "4px", width: "24px", height: "24px" }}
					></AppUnknown>
				)}
				<div style={{ marginLeft: "14px" }}>{name}</div>
			</div>
			{action}
		</ListItem>
	);
}

function CategoryBlock({
	modalAction,
	listAction,
	category,
	applicationList,
	onUpdate,
}: {
	modalAction: "block" | "allow";
	listAction: "add" | "remove";
	category: "Browser" | "Communication" | "Screen Sharing" | "Other";
	applicationList: Application[];
	onUpdate: (applications: Application[]) => void;
}) {
	const [fold, setFold] = useState(false);
	const currentApplicationList = applicationList.filter((app) => app.type === category);

	if (currentApplicationList.length === 0) {
		return <></>;
	}
	const buttonIcon = listAction === "add" ? <Add /> : <Remove />;
	return (
		<>
			<ListItem className={styles.applicationRow}>
				<div style={{ display: "flex" }}>
					<SvgIcon
						onClick={() => setFold(!fold)}
						sx={{ color: "primary.main" }}
						style={{ cursor: "pointer", marginTop: "7px" }}
					>
						{fold ? <CaretClose /> : <CaretOpen />}
					</SvgIcon>
					<div style={{ marginTop: "3px" }}>
						<strong>{category}</strong>
					</div>
				</div>
				<CoreIconButton
					variant="plain"
					onClick={() => onUpdate(applicationList.filter((app) => app.type === category))}
					sx={{ color: "primary.main" }}
					aria-label={i18n.t(
						`editQuizPage.configurationStep.applicationBlockModal.ariaLabels.moveCategory.${modalAction}.${listAction}`,
						{ category },
					)}
				>
					{buttonIcon}
				</CoreIconButton>
			</ListItem>
			{!fold &&
				currentApplicationList.map((application) => (
					<ApplicationRow
						key={application.name}
						application={application}
						action={
							<CoreIconButton
								variant="plain"
								onClick={() => onUpdate([application])}
								sx={{ color: "primary.main" }}
								aria-label={i18n.t(
									`editQuizPage.configurationStep.applicationBlockModal.ariaLabels.moveApp.${modalAction}.${listAction}`,
									{
										appName: application.name,
									},
								)}
							>
								{buttonIcon}
							</CoreIconButton>
						}
					/>
				))}
		</>
	);
}

function ApplicationListTable({
	modalAction,
	listAction,
	applicationList,
	onUpdate,
}: {
	modalAction: "block" | "allow";
	listAction: "add" | "remove";
	applicationList: Application[];
	onUpdate: (applications: Application[]) => void;
}) {
	return (
		<div className={styles.listPanel} style={{ gap: "20px" }}>
			<List className={styles.applicationList} sx={{ height: LIST_PANEL_HEIGHT }}>
				<CategoryBlock
					modalAction={modalAction}
					listAction={listAction}
					category="Browser"
					applicationList={applicationList}
					onUpdate={onUpdate}
				/>
				<CategoryBlock
					modalAction={modalAction}
					listAction={listAction}
					category="Communication"
					applicationList={applicationList}
					onUpdate={onUpdate}
				/>
				<CategoryBlock
					modalAction={modalAction}
					listAction={listAction}
					category="Screen Sharing"
					applicationList={applicationList}
					onUpdate={onUpdate}
				/>
				<CategoryBlock
					modalAction={modalAction}
					listAction={listAction}
					category="Other"
					applicationList={applicationList}
					onUpdate={onUpdate}
				/>
			</List>
		</div>
	);
}

function convertCurrentOptionToTwoList(
	applicationList: string[],
	applications: Application[],
): [Application[], Application[]] {
	const blockedApplicationList: Application[] = [];
	let availableApplicationList: Application[] = [...applications];
	for (const application of applicationList) {
		const foundApplication = applications.find((app) => app.processName.includes(application));
		if (foundApplication) {
			if (blockedApplicationList.includes(foundApplication)) continue;
			blockedApplicationList.push(foundApplication);
			availableApplicationList = availableApplicationList.filter((app) => app.name !== foundApplication.name);
		} else {
			blockedApplicationList.push({
				name: application,
				processName: [application],
				type: "Other",
			});
		}
	}
	return [availableApplicationList, blockedApplicationList];
}

export default function ApplicationListModal({
	onClose,
	allApplications,
	action,
	initialSelectedProcessNames,
	onSave,
}: {
	onClose: () => void;
	allApplications: Application[];
	action: "block" | "allow";
	initialSelectedProcessNames: string[];
	onSave: (processNames: string[]) => void;
}) {
	const [showAddModal, setShowAddModal] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [initialUnselectedApplications, initialSelectedApplications] = convertCurrentOptionToTwoList(
		initialSelectedProcessNames,
		allApplications,
	);
	const [selectedApplications, setSelectedApplications] = useState<Application[]>(initialSelectedApplications);
	const [unselectedApplications, setUnselectedApplications] = useState<Application[]>(initialUnselectedApplications);
	const [saving, setSaving] = useState(false);
	const isMobileLayout = useMobileLayout();

	const searchFilter = searchText.toLowerCase();
	const sortFunction = (a: Application, b: Application) => a.name.localeCompare(b.name);
	const displayApplication = unselectedApplications
		.filter((application) => application.name.toLowerCase().includes(searchFilter))
		.sort(sortFunction);
	const controlId = useId();

	return (
		<CoreModal
			open
			onClose={onClose}
			title={
				isMobileLayout ? undefined : i18n.t(`editQuizPage.configurationStep.applicationBlockModal.title.${action}`)
			}
			minWidth={isMobileLayout ? undefined : "min(65%, 1000px)"}
			minHeight={isMobileLayout ? undefined : "min(90%, 900px)"}
			widthFixed
			heightFixed
			padding={isMobileLayout ? undefined : "0 12px 0 24px"}
			disableTopCloseButton={saving}
			fullScreen={isMobileLayout}
			showTopCloseButton={!isMobileLayout}
			dialogContentBackground={isMobileLayout ? "#f4f4f4" : undefined}
			buttons={[
				{
					text: "Cancel",
					color: "secondary",
					style: { marginLeft: isMobileLayout ? "auto" : undefined },
					onClick: onClose,
					disabled: saving,
				},
				{
					text: saving ? "Saving..." : "Save",
					color: "primary",
					onClick: () => {
						setSaving(true);
						onSave(Array.from(new Set(selectedApplications.map((application) => application.processName).flat())));
						onClose();
					},
					disabled: saving,
				},
			]}
		>
			<ConditionalWrapper
				condition={isMobileLayout}
				whenTrue={(children) => (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							width: "100%",
							gap: "10px",
							background: "f4f4f4",
						}}
					>
						{children}
					</div>
				)}
			>
				{isMobileLayout && (
					<CoreTitleCard
						title={"Block Application List"}
						description={"Add a list of applications that will be blocked during the assessment."}
						style={{ padding: "15px 10px 10px 10px", marginTop: "10px" }}
						slotProps={{ title: { style: { color: "#42296E" } }, text: { style: { gap: "5px" } } }}
					/>
				)}
				<ConditionalWrapper
					condition={isMobileLayout}
					whenTrue={(children) => (
						<CorePaper
							component="section"
							sx={{
								display: "flex",
								flexWrap: "wrap",
								justifyContent: "space-between",
								width: "100%",
								columnGap: "24px",
								padding: "10px",
								boxSizing: "border-box",
							}}
						>
							{children}
						</CorePaper>
					)}
				>
					{/* Text Content */}
					<div
						style={{
							fontSize: 15,
							fontWeight: 400,
							display: "flex",
							flexDirection: "column",
							gap: 16,
							padding: "20px 20px 0px 20px",
							justifyContent: "flex-start",
						}}
					>
						<div
							style={isMobileLayout ? { gap: "8px", width: "100%" } : undefined}
							onClick={(e) => {
								e.preventDefault();
								setShowAddModal(true);
							}}
						>
							<span>
								<Trans
									i18n={i18n}
									i18nKey={`editQuizPage.configurationStep.applicationBlockModal.description.${action}`}
									components={{
										appLink: (
											<Link
												href="#"
												className={styles["application-link"]}
												onClick={(e) => {
													e.preventDefault();
													setShowAddModal(true);
												}}
											/>
										),
									}}
								/>
							</span>
						</div>
						{action === "allow" && (
							<div>
								<span style={{ fontWeight: "bold" }}>
									{i18n.t("editQuizPage.configurationStep.applicationBlockModal.note")}
								</span>{" "}
								{i18n.t("editQuizPage.configurationStep.applicationBlockModal.otherApplicationsBlocked")}
							</div>
						)}
					</div>

					{/* UX Content */}
					<div
						style={
							isMobileLayout
								? {
										display: "flex",
										flexWrap: "wrap",
										justifyContent: "center",
										columnGap: "24px",
										maxWidth: "100%",
									}
								: {
										padding: "20px 20px",
										display: "flex",
										flexWrap: "wrap",
										justifyContent: "center",
										columnGap: "24px",
									}
						}
					>
						<div style={{ flexBasis: "100%", justifyContent: "center", display: "flex" }}>
							<CoreSearchFieldPaper>
								<CoreSearchField
									autoComplete="off"
									label={i18n.t("editQuizPage.configurationStep.applicationBlockModal.search")}
									onSearch={(value) => setSearchText(value)}
									value={searchText}
									ariaControls={controlId}
									totalResults={displayApplication.length}
								/>
							</CoreSearchFieldPaper>
						</div>
						<div
							style={
								isMobileLayout
									? {
											justifyContent: "center",
											display: "flex",
											flexWrap: "wrap",
											width: "100%",
										}
									: {
											boxSizing: "border-box",
											flexBasis: "45%",
											justifyContent: "start",
											display: "flex",
											flexWrap: "wrap",
											padding: "0px",
										}
							}
							id={controlId}
						>
							<Typography variant="h3" className={styles.header}>
								Application List
							</Typography>
							{displayApplication.length === 0 ? (
								<div style={{ flexBasis: "100%" }}>
									<div className={styles.applicationList}>
										<div
											style={{
												display: "flex",
												flexWrap: "wrap",
												justifyContent: "center",
												alignContent: "center",
												gap: 15,
												height: LIST_PANEL_HEIGHT,
												padding: "0 20px",
											}}
										>
											<img src={application_block_icon} alt="" style={{ flexBasis: "100%", maxHeight: "50%" }} />
											<CoreNoResultsText
												header={i18n.t(
													searchText !== "" && unselectedApplications.length !== 0
														? `editQuizPage.configurationStep.applicationBlockModal.noApplications.${action}.search.title`
														: `editQuizPage.configurationStep.applicationBlockModal.noApplications.${action}.available.title`,
												)}
												slotProps={{ header: { component: "div" } }}
											>
												<div
													style={isMobileLayout ? { gap: "8px", width: "100%" } : undefined}
													onClick={(e) => {
														e.preventDefault();
														setShowAddModal(true);
													}}
												>
													<span>
														<Trans
															i18n={i18n}
															i18nKey={
																searchText !== "" && unselectedApplications.length !== 0
																	? `editQuizPage.configurationStep.applicationBlockModal.noApplications.${action}.search.description`
																	: `editQuizPage.configurationStep.applicationBlockModal.noApplications.${action}.available.description`
															}
															components={{
																appLink: (
																	<Link
																		href="#"
																		className={styles["application-link"]}
																		onClick={(e) => {
																			e.preventDefault();
																			setShowAddModal(true);
																		}}
																	/>
																),
															}}
														/>
													</span>
												</div>
											</CoreNoResultsText>
										</div>
									</div>
								</div>
							) : (
								<ApplicationListTable
									modalAction={action}
									listAction="add"
									applicationList={displayApplication}
									onUpdate={(applications) => {
										setSelectedApplications(
											selectedApplications.concat(applications.filter((app) => !selectedApplications.includes(app))),
										);
										setUnselectedApplications(
											unselectedApplications.filter((s) => applications.every((app) => app.name !== s.name)),
										);
									}}
								/>
							)}
						</div>
						<div
							style={
								isMobileLayout
									? {
											justifyContent: "center",
											display: "flex",
											flexWrap: "wrap",
											width: "100%",
										}
									: {
											boxSizing: "border-box",
											flexBasis: "45%",
											justifyContent: "start",
											display: "flex",
											flexWrap: "wrap",
											padding: "0px",
										}
							}
						>
							<Typography variant="h3" className={styles.header}>
								{i18n.t(`editQuizPage.configurationStep.applicationBlockModal.selectedList.${action}`)}
							</Typography>
							{selectedApplications.length === 0 ? (
								<div style={{ flexBasis: "100%" }}>
									<div className={styles.applicationList}>
										<div
											style={{
												display: "flex",
												flexWrap: "wrap",
												justifyContent: "center",
												alignContent: "center",
												gap: 15,
												height: LIST_PANEL_HEIGHT,
												padding: "0 20px",
											}}
										>
											<img src={application_block_icon} alt="" style={{ flexBasis: "100%", maxHeight: "50%" }} />
											<CoreNoResultsText
												header={i18n.t(
													`editQuizPage.configurationStep.applicationBlockModal.noApplications.${action}.added.title`,
												)}
												slotProps={{ header: { component: "div" } }}
											>
												{i18n.t(
													`editQuizPage.configurationStep.applicationBlockModal.noApplications.${action}.added.description`,
												)}
											</CoreNoResultsText>
										</div>
									</div>
								</div>
							) : (
								<ApplicationListTable
									modalAction={action}
									listAction="remove"
									applicationList={selectedApplications}
									onUpdate={(applications) => {
										setUnselectedApplications(
											unselectedApplications.concat(
												applications.filter((app) => !unselectedApplications.includes(app)),
											),
										);
										setSelectedApplications(
											selectedApplications.filter((s) => applications.every((app) => app.name !== s.name)),
										);
									}}
								/>
							)}
						</div>
						{showAddModal && (
							<AddProcessNameModal
								action={action}
								setShowAddModal={setShowAddModal}
								setBlockedApplicationList={setSelectedApplications}
								blockedApplicationList={selectedApplications}
							/>
						)}
					</div>
				</ConditionalWrapper>
			</ConditionalWrapper>
		</CoreModal>
	);
}

function AddProcessNameModal({
	action,
	setShowAddModal,
	setBlockedApplicationList,
	blockedApplicationList,
}: {
	action: "block" | "allow";
	setShowAddModal: React.Dispatch<React.SetStateAction<boolean>>;
	setBlockedApplicationList: React.Dispatch<React.SetStateAction<Application[]>>;
	blockedApplicationList: Application[];
}) {
	const [processName, setProcessName] = useState<string>("");
	const isMobileLayout = useMobileLayout();
	const textFieldId = useId();
	const windowAriaLabel = i18n.t(
		"editQuizPage.configurationStep.applicationBlockModal.addApplicationModal.osLinkAria",
		{ os: "Windows" },
	);
	const macAriaLabel = i18n.t("editQuizPage.configurationStep.applicationBlockModal.addApplicationModal.osLinkAria", {
		os: "Mac",
	});

	return (
		<CoreModal
			open
			onClose={() => setShowAddModal(false)}
			title={"Add Application"}
			minWidth={isMobileLayout ? "100%" : "min(60%, 600px)"}
			widthFixed
			contentMatchModalHeight
			padding={isMobileLayout ? "10px 24px" : undefined}
			buttons={[
				{
					text: "Cancel",
					color: "secondary",
					onClick: () => setShowAddModal(false),
				},
				{
					text: "Add",
					color: "primary",
					onClick: () => {
						const processNames = [processName];
						if (processName.includes(".")) {
							processNames.push(processName.split(".")[0]);
						}
						setBlockedApplicationList([
							...blockedApplicationList,
							{ processName: processNames, name: processName, type: "Other" },
						]);
						setShowAddModal(false);
					},
					disabled: processName === "",
				},
			]}
		>
			<p style={{ marginTop: 0 }}>
				{i18n.t(`editQuizPage.configurationStep.applicationBlockModal.addApplicationModal.firstParagraph.${action}`)}
			</p>

			<Trans
				i18n={i18n}
				i18nKey="editQuizPage.configurationStep.applicationBlockModal.addApplicationModal.secondParagraph"
				components={{
					bold: <strong />,
					windowsLink: (
						<Link
							href="https://support.yuja.com/hc/en-us/articles/19811973185943-Creating-a-Blocked-Application-List-for-the-Proctored-Assessment#h_01HPSEPZK158TSA1P74ZBMHYWC"
							target="_blank"
							rel="noopener noreferrer"
							className={styles["application-link"]}
							aria-label={windowAriaLabel}
						/>
					),
					macLink: (
						<Link
							href="https://support.yuja.com/hc/en-us/articles/19811973185943-Creating-a-Blocked-Application-List-for-the-Proctored-Assessment#01JWGV5CPBYPDYA6XAMK8MQT2N"
							target="_blank"
							rel="noopener noreferrer"
							className={styles["application-link"]}
							aria-label={macAriaLabel}
						/>
					),
				}}
			/>

			<Box
				sx={{
					display: "flex",
					flexDirection: isMobileLayout ? "column" : undefined,
					alignItems: "center",
					width: "100%",
					mt: "15px",
				}}
			>
				<Typography
					sx={{
						whiteSpace: "nowrap",
						marginRight: 2,
						fontFamily: "Inter",
						fontWeight: 600,
						fontStyle: "normal",
						fontSize: "15px",
						lineHeight: "150%",
						letterSpacing: "0px",
						color: "#000000",
					}}
					id={textFieldId}
				>
					{i18n.t("editQuizPage.configurationStep.applicationBlockModal.addApplicationModal.applicationNameLabel")}
				</Typography>
				<CoreTextField
					value={processName}
					onChange={(e) => setProcessName(e.target.value)}
					sx={{ width: "100%" }}
					inputProps={{ "aria-labelledby": textFieldId }}
				/>
			</Box>
		</CoreModal>
	);
}
